/**
 * Evidence Quality Dashboard
 * 
 * Dashboard showing evidence coverage and quality metrics.
 * Part of Workstream 2: Evidence System
 */

import { useMemo } from 'react';
import {
  ChartBarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useEvidenceStore } from '../../../stores/prioritization/evidenceStore';
import { getEvidenceTypeLabel, getReliabilityLabel } from '../../../types/prioritization/evidence';
import type { EvidenceType, EvidenceReliability } from '../../../types/prioritization/evidence';

export function EvidenceQuality() {
  // Get raw evidence data (stable reference)
  const evidence = useEvidenceStore(state => state.evidence);
  
  // Compute stats with useMemo to avoid infinite loops
  const stats = useMemo(() => {
    const emptyStats = {
      totalEvidence: 0,
      itemsWithEvidence: 0,
      itemsWithoutEvidence: 0,
      itemsWithOnlyAssumptions: 0,
      averageEvidencePerItem: 0,
      verifiedPercentage: 0,
      recentlyAdded: 0,
      byType: {
        data: 0, research: 0, expert_opinion: 0, customer_feedback: 0,
        market_signal: 0, competitor_intel: 0, internal_observation: 0, assumption: 0,
      } as Record<EvidenceType, number>,
      byReliability: {
        verified: 0, corroborated: 0, single_source: 0, anecdotal: 0, speculative: 0,
      } as Record<EvidenceReliability, number>,
      topSources: [] as Array<{ source: string; count: number }>,
    };
    
    // Defensive check for undefined/null evidence
    if (!evidence || typeof evidence !== 'object') {
      return emptyStats;
    }
    
    const evidenceList = Object.values(evidence);
    if (evidenceList.length === 0) {
      return emptyStats;
    }
    
    const byType: Record<EvidenceType, number> = {
      data: 0, research: 0, expert_opinion: 0, customer_feedback: 0,
      market_signal: 0, competitor_intel: 0, internal_observation: 0, assumption: 0,
    };
    const byReliability: Record<EvidenceReliability, number> = {
      verified: 0, corroborated: 0, single_source: 0, anecdotal: 0, speculative: 0,
    };
    const sourceCounts: Record<string, number> = {};
    
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    let recentlyAdded = 0;
    
    for (const e of evidenceList) {
      if (e.type in byType) byType[e.type]++;
      if (e.reliability in byReliability) byReliability[e.reliability]++;
      if (e.source) {
        sourceCounts[e.source] = (sourceCounts[e.source] || 0) + 1;
      }
      if (e.addedAt && new Date(e.addedAt) >= sevenDaysAgo) {
        recentlyAdded++;
      }
    }
    
    // Get unique items with evidence
    const itemsWithEvidenceSet = new Set(evidenceList.map(e => e.itemId));
    const itemsWithEvidence = itemsWithEvidenceSet.size;
    
    // Items with only assumptions
    const itemEvidenceTypes: Record<string, Set<string>> = {};
    for (const e of evidenceList) {
      if (!itemEvidenceTypes[e.itemId]) {
        itemEvidenceTypes[e.itemId] = new Set();
      }
      itemEvidenceTypes[e.itemId].add(e.type);
    }
    const itemsWithOnlyAssumptions = Object.values(itemEvidenceTypes).filter(
      types => types.size === 1 && types.has('assumption')
    ).length;
    
    const verifiedCount = evidenceList.filter(e => e.verifiedAt).length;
    
    // Top sources
    const topSources = Object.entries(sourceCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([source, count]) => ({ source, count }));
    
    return {
      totalEvidence: evidenceList.length,
      itemsWithEvidence,
      itemsWithoutEvidence: 0, // We don't know total items here
      itemsWithOnlyAssumptions,
      averageEvidencePerItem: itemsWithEvidence > 0 ? evidenceList.length / itemsWithEvidence : 0,
      verifiedPercentage: evidenceList.length > 0 ? (verifiedCount / evidenceList.length) * 100 : 0,
      recentlyAdded,
      byType,
      byReliability,
      topSources,
    };
  }, [evidence]);
  
  const typeColors: Record<EvidenceType, string> = {
    data: 'bg-[var(--slate)]',
    research: 'bg-[var(--slate)]',
    expert_opinion: 'bg-[var(--green)]',
    customer_feedback: 'bg-[var(--mustard)]',
    market_signal: 'bg-[var(--slate)]',
    competitor_intel: 'bg-[var(--coral)]',
    internal_observation: 'bg-[var(--surface)]',
    assumption: 'bg-[var(--mustard)]',
  };
  
  const reliabilityColors: Record<EvidenceReliability, string> = {
    verified: 'bg-[var(--green)]',
    corroborated: 'bg-[var(--slate)]',
    single_source: 'bg-[var(--mustard)]',
    anecdotal: 'bg-[var(--mustard)]',
    speculative: 'bg-[var(--coral)]',
  };
  
  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--text-pri)]">Evidence Quality</h1>
          <p className="mt-1 text-sm text-[var(--text-ter)]">
            Monitor evidence coverage and quality across all items
          </p>
        </div>
      </div>
      
      {/* Key Metrics */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-[var(--bg-raised)] p-6 shadow">
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 text-[var(--text-ter)]" />
            <div className="ml-4">
              <p className="text-sm font-medium text-[var(--text-ter)]">Total Evidence</p>
              <p className="text-2xl font-semibold text-[var(--text-pri)]">{stats.totalEvidence}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[var(--bg-raised)] p-6 shadow">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-[var(--text-ter)]" />
            <div className="ml-4">
              <p className="text-sm font-medium text-[var(--text-ter)]">Items with Evidence</p>
              <p className="text-2xl font-semibold text-[var(--text-pri)]">{stats.itemsWithEvidence}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[var(--bg-raised)] p-6 shadow">
          <div className="flex items-center">
            <CheckBadgeIcon className="h-8 w-8 text-[var(--green)]" />
            <div className="ml-4">
              <p className="text-sm font-medium text-[var(--text-ter)]">Verified</p>
              <p className="text-2xl font-semibold text-[var(--text-pri)]">
                {stats.verifiedPercentage.toFixed(0)}%
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-[var(--bg-raised)] p-6 shadow">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-[var(--mustard)]" />
            <div className="ml-4">
              <p className="text-sm font-medium text-[var(--text-ter)]">Only Assumptions</p>
              <p className="text-2xl font-semibold text-[var(--text-pri)]">{stats.itemsWithOnlyAssumptions}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Charts */}
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {/* By Type */}
        <div className="bg-[var(--bg-raised)] p-6 shadow">
          <h3 className="text-lg font-medium text-[var(--text-pri)]">Evidence by Type</h3>
          <div className="mt-4 space-y-3">
            {(Object.entries(stats.byType) as [EvidenceType, number][])
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => {
                const percentage = stats.totalEvidence > 0 
                  ? (count / stats.totalEvidence) * 100 
                  : 0;
                  
                return (
                  <div key={type}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[var(--text-sec)]">{getEvidenceTypeLabel(type)}</span>
                      <span className="font-medium text-[var(--text-pri)]">{count}</span>
                    </div>
                    <div className="mt-1 h-2 w-full rounded-full bg-[var(--hover-tint)]">
                      <div
                        className={`h-2 rounded-full ${typeColors[type]}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        
        {/* By Reliability */}
        <div className="bg-[var(--bg-raised)] p-6 shadow">
          <h3 className="text-lg font-medium text-[var(--text-pri)]">Evidence by Reliability</h3>
          <div className="mt-4 space-y-3">
            {(Object.entries(stats.byReliability) as [EvidenceReliability, number][])
              .sort((a, b) => {
                const order = ['verified', 'corroborated', 'single_source', 'anecdotal', 'speculative'];
                return order.indexOf(a[0]) - order.indexOf(b[0]);
              })
              .map(([reliability, count]) => {
                const percentage = stats.totalEvidence > 0 
                  ? (count / stats.totalEvidence) * 100 
                  : 0;
                  
                return (
                  <div key={reliability}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[var(--text-sec)]">{getReliabilityLabel(reliability)}</span>
                      <span className="font-medium text-[var(--text-pri)]">{count}</span>
                    </div>
                    <div className="mt-1 h-2 w-full rounded-full bg-[var(--hover-tint)]">
                      <div
                        className={`h-2 rounded-full ${reliabilityColors[reliability]}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
      
      {/* Top Sources */}
      <div className="mt-8 bg-[var(--bg-raised)] p-6 shadow">
        <h3 className="text-lg font-medium text-[var(--text-pri)]">Top Sources</h3>
        {stats.topSources.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--text-ter)]">No evidence sources yet</p>
        ) : (
          <div className="mt-4 overflow-hidden">
            <table className="min-w-full divide-y divide-[var(--rule)]">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-ter)]">
                    Source
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-[var(--text-ter)]">
                    Count
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-[var(--text-ter)]">
                    % of Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)]">
                {stats.topSources.map((source, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 text-sm text-[var(--text-pri)]">{source.source}</td>
                    <td className="px-4 py-3 text-right text-sm text-[var(--text-pri)]">{source.count}</td>
                    <td className="px-4 py-3 text-right text-sm text-[var(--text-ter)]">
                      {stats.totalEvidence > 0 
                        ? ((source.count / stats.totalEvidence) * 100).toFixed(1) 
                        : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Alerts */}
      {(stats.itemsWithOnlyAssumptions > 0 || stats.itemsWithoutEvidence > 0) && (
        <div className="mt-8 space-y-4">
          {stats.itemsWithOnlyAssumptions > 0 && (
            <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-[var(--mustard)]" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-[var(--mustard)]">
                    Items with Only Assumptions
                  </h4>
                  <p className="mt-1 text-sm text-[var(--mustard)]">
                    {stats.itemsWithOnlyAssumptions} item{stats.itemsWithOnlyAssumptions !== 1 ? 's' : ''} have 
                    only assumptions as evidence. Consider adding verified data or research.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {stats.itemsWithoutEvidence > 0 && (
            <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-4">
              <div className="flex">
                <XCircleIcon className="h-5 w-5 text-[var(--coral)]" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-[var(--coral)]">
                    Items Without Evidence
                  </h4>
                  <p className="mt-1 text-sm text-[var(--coral)]">
                    {stats.itemsWithoutEvidence} item{stats.itemsWithoutEvidence !== 1 ? 's' : ''} have 
                    no evidence attached. This may affect prioritization quality.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Recent Activity */}
      <div className="mt-8 bg-[var(--bg-raised)] p-6 shadow">
        <h3 className="text-lg font-medium text-[var(--text-pri)]">Recent Activity</h3>
        <p className="mt-2 text-sm text-[var(--text-ter)]">
          {stats.recentlyAdded} evidence item{stats.recentlyAdded !== 1 ? 's' : ''} added in the last 7 days
        </p>
        <div className="mt-2 text-sm text-[var(--text-ter)]">
          Average {stats.averageEvidencePerItem.toFixed(1)} evidence items per item with evidence
        </div>
      </div>
    </div>
  );
}

export default EvidenceQuality;
